// Each dash is drawn as a polyline with this many segments, so it can follow a corner.
const DASH_SEGMENTS = 4;

/**
 * Build an SVG path of evenly spaced dashes around a rounded square centred on the origin,
 * with a dash centred on the top edge's midpoint. Each dash is its own short polyline. A
 * single stroke-dasharray would leave a small gap or overlap where the outline starts and
 * ends, because the browser measures the curved corners only approximately.
 */
const roundedSquareDashes = (size: number, radius: number, count: number) => {
  const half = size / 2;
  const straight = half - radius;
  const arc = (Math.PI * radius) / 2;
  // The outline is four identical quarters, each running clockwise from one side's
  // midpoint to the next.
  const quarter = 2 * straight + arc;
  const perimeter = 4 * quarter;

  const pointAt = (distance: number): [number, number] => {
    const t = ((distance % perimeter) + perimeter) % perimeter;
    const quarterIndex = Math.floor(t / quarter);
    const u = t - quarterIndex * quarter;
    let x: number;
    let y: number;
    if (u <= straight) {
      [x, y] = [u, -half];
    } else if (u <= straight + arc) {
      const angle = (u - straight) / radius;
      [x, y] = [straight + radius * Math.sin(angle), -straight - radius * Math.cos(angle)];
    } else {
      [x, y] = [half, -straight + (u - straight - arc)];
    }
    // Rotate 90° clockwise into the right quarter.
    for (let i = 0; i < quarterIndex; i++) {
      [x, y] = [-y, x];
    }
    return [x, y];
  };

  const spacing = perimeter / count;
  const dashLength = spacing / 2;
  let d = "";
  for (let i = 0; i < count; i++) {
    const start = i * spacing - dashLength / 2;
    for (let j = 0; j <= DASH_SEGMENTS; j++) {
      const [x, y] = pointAt(start + (dashLength * j) / DASH_SEGMENTS);
      d += `${j === 0 ? "M" : "L"}${x.toFixed(3)} ${y.toFixed(3)}`;
    }
  }
  return d;
};

export default roundedSquareDashes;
